import { CommandBase } from "../CommandBase.js";

export class ManCommand extends CommandBase {
    async execute(args) {
        const target = args[0];
        
        if (!target) {
            this.write("What manual page do you want? Usage: man [command/project]", "text-muted");
            return;
        }

        // 1. Try terminal.json man_pages FIRST
        const manPage = this.contentManager.getManPage(target);
        
        if (manPage) {
            this.renderManPageFromJSON(manPage);
            return;
        }

        // 2. Check if it's a registered command (dynamically from CommandRegistry)
        const commandObj = this.terminal.commandRegistry.get(target.toLowerCase());
        
        if (commandObj) {
            // It's a real command, but no manual exists
            this.write(`No manual entry for ${target}`, "text-muted");
            this.write(`Command exists but documentation is incomplete.`, "text-dim");
            this.write(`Try 'help' for a command overview.`, "text-dim");
            return;
        }

        // 3. Try loading as project (last resort)
        try {
            const project = await this.contentManager.getProject(target);
            if (project) {
                const projectManPage = this.generateProjectManPage(project);
                this.renderPage(projectManPage);
                return;
            }
        } catch (error) {
            // Project not found, continue to error
        }

        // 4. Not found anywhere
        this.write(`No manual entry for ${target}`, "text-red");
        this.write(`Try 'help' for available commands or 'ls projects' for project list.`, "text-muted");
    }


    /**
     * Generates a Man Page object from raw Project Data
     */
    generateProjectManPage(project) {
        // Format highlights as a list if they exist
        const highlights = project.metadata?.highlights || [];
        const highlightsSection = highlights.length > 0 
            ? highlights.map(h => `• ${h}`).join('\n    ') 
            : "No specific highlights documented.";

        // ✅ Use the body or the detailed description for the BACKGROUND section
        const backgroundContent = project.body_html || project.description || "Full background details are not available for this project.";


        return {
            name: project.title.toUpperCase(),
            sections: [
                {
                    title: "NAME",
                    content: `${project.title} - ${project.status.toUpperCase()}`
                },
                {
                    title: "SYNOPSIS",
                    content: `open ${project.links?.demo || 'N/A'}`
                },
                // ✅ NEW SECTION FOR FULL DESCRIPTION
                {
                    title: "BACKGROUND",
                    // This will render the full multi-line explanation.
                    content: backgroundContent 
                },
                {
                    title: "STACK",
                    content: project.tags ? project.tags.join(', ') : "Unknown"
                },
                {
                    title: "HIGHLIGHTS",
                    content: highlightsSection
                },
                {
                    title: "ACCESS",
                    content: `Source: ${project.links?.github || 'N/A'}\n    Demo:   ${project.links?.demo || 'N/A'}`
                }
            ]
        };
    }

    /**
     * Renders the standardized Man Page format
     */
    renderPage(page) {
        // Build the HTML string in memory instead of writing it line-by-line
        let bufferHtml = '';

        // Helper to append HTML
        const append = (html) => bufferHtml += html;

        if (!page.sections) {
            // Legacy Fallback
            append(`<div class="text-accent">NAME</div>`);
            append(`<div>    ${page.name} - ${page.description}</div><br>`);
            if(page.usage) {
                append(`<div class="text-accent">SYNOPSIS</div>`);
                append(`<div>    ${page.usage}</div><br>`);
            }
        } else {
            // Rich Sections
            page.sections.forEach(section => {
                append(`<div class="text-accent">${section.title}</div>`);
                const lines = section.content.split('\n');
                lines.forEach(line => {
                    append(`<div>    ${line}</div>`);
                });
                append(`<br>`);
            });
        }

        // ✅ CALL THE TERMINAL'S BUFFER MODE
        this.terminal.enterBufferMode(bufferHtml, `MANUAL: ${page.name} // PRESS 'Q' TO QUIT`);
    }

    /**
     * Renders a man page from terminal.json format
     * @param {Object} manPage - { name, description, usage, examples }
     */
    renderManPageFromJSON(manPage) {
        let html = '<div class="man-page">';
        html += `<div class="man-header">${manPage.name.toUpperCase()}(1)</div>`;
        
        // NAME section
        html += '<div class="man-section">';
        html += '<div class="man-section-title">NAME</div>';
        html += `<div class="man-section-content">${manPage.name} - ${manPage.description}</div>`;
        html += '</div>';
        
        // SYNOPSIS section
        html += '<div class="man-section">';
        html += '<div class="man-section-title">SYNOPSIS</div>';
        html += `<div class="man-section-content">${manPage.usage}</div>`;
        html += '</div>';
        
        // EXAMPLES section (if available)
        if (manPage.examples && manPage.examples.length > 0) {
            html += '<div class="man-section">';
            html += '<div class="man-section-title">EXAMPLES</div>';
            html += '<div class="man-section-content">';
            manPage.examples.forEach(ex => {
                html += `${ex}<br>`;
            });
            html += '</div>';
            html += '</div>';
        }
        
        html += '</div>';
        
        this.terminal.enterBufferMode(html, `Manual: ${manPage.name} - Press 'q' to quit`);
    }

}